import type { GitpodAPI } from "@/api";
import { ConnectError, Code, type CallOptions } from "@connectrpc/connect";
import { AccountService } from "gitpod-next-api/gitpod/v1/account_connect";
import { EnvironmentAutomationService } from "gitpod-next-api/gitpod/v1/environment_automation_connect";
import { EnvironmentService } from "gitpod-next-api/gitpod/v1/environment_connect";
import { EventService } from "gitpod-next-api/gitpod/v1/event_connect";

/**
 * VSCode IPC Observability
 *
 * Enable by setting localStorage key: localStorage.setItem("gitpod.debug.vscode-ipc", "true")
 * Disable by removing: localStorage.removeItem("gitpod.debug.vscode-ipc")
 *
 * When enabled:
 * - Logs all IPC messages to console with timing statistics
 * - Sends Segment events for each IPC message (if segmentTrack is provided)
 *
 * Debug in console:
 *   window.__getVSCodeIPCStats?.()   - Get current statistics
 *   window.__resetVSCodeIPCStats?.() - Reset statistics
 */
const VSCODE_IPC_DEBUG_KEY = "gitpod.debug.vscode-ipc";

type IPCEventType =
    | "messagePort"
    | "openFile"
    | "enableFollowMode"
    | "disableFollowMode"
    | "resolve"
    | "setVisibilityState"
    | "rpcRequest"
    | "rpcResponse";

type IPCStats = {
    counts: Record<IPCEventType, number>;
    startTime: number;
    lastEventTime: number;
};

const ipcStats: IPCStats = {
    counts: {
        messagePort: 0,
        openFile: 0,
        enableFollowMode: 0,
        disableFollowMode: 0,
        resolve: 0,
        setVisibilityState: 0,
        rpcRequest: 0,
        rpcResponse: 0,
    },
    startTime: Date.now(),
    lastEventTime: Date.now(),
};

// Cache debug mode state with debounced localStorage access
let debugEnabledCache: boolean | null = null;
let debugCheckTimeout: ReturnType<typeof setTimeout> | null = null;

function isIPCDebugEnabled(): boolean {
    // Return cached value if available
    if (debugEnabledCache !== null) {
        return debugEnabledCache;
    }

    // Debounce localStorage access - schedule a check in 10s
    if (debugCheckTimeout === null) {
        debugCheckTimeout = setTimeout(() => {
            debugCheckTimeout = null;
            debugEnabledCache = null; // Clear cache to force re-check
        }, 10000);
    }

    // Read from localStorage and cache the result
    try {
        debugEnabledCache = localStorage.getItem(VSCODE_IPC_DEBUG_KEY) === "true";
        return debugEnabledCache;
    } catch {
        debugEnabledCache = false;
        return false;
    }
}

/** Segment tracking function type - matches useSegmentTrack return type */
export type SegmentTrackFn = (eventName: string, properties?: Readonly<Record<string, unknown>>) => void;

function trackIPCEvent(
    eventType: IPCEventType,
    direction: "outbound" | "inbound",
    segmentTrack?: SegmentTrackFn,
    details?: Record<string, unknown>,
): void {
    // TEMP DEBUG: Log every call to trackIPCEvent
    console.log("[VSCode IPC DEBUG] trackIPCEvent called:", {
        eventType,
        direction,
        hasSegmentTrack: !!segmentTrack,
        debugEnabled: isIPCDebugEnabled(),
    });

    if (!isIPCDebugEnabled()) {
        return;
    }

    const now = Date.now();
    const timeSinceStart = now - ipcStats.startTime;
    const timeSinceLast = now - ipcStats.lastEventTime;
    ipcStats.lastEventTime = now;
    ipcStats.counts[eventType]++;

    const totalEvents = Object.values(ipcStats.counts).reduce((a, b) => a + b, 0);
    const eventsPerSecond = (totalEvents / (timeSinceStart / 1000)).toFixed(2);

    const logData = {
        eventType,
        direction,
        details,
        timing: {
            sinceStartMs: timeSinceStart,
            sinceLastMs: timeSinceLast,
            timestamp: new Date().toISOString(),
        },
        stats: {
            totalEvents,
            eventCounts: { ...ipcStats.counts },
            eventsPerSecond,
        },
    };

    console.log(`[VSCode IPC] ${direction.toUpperCase()} ${eventType}`, logData);

    // Send to Segment if available
    if (segmentTrack) {
        console.log("[VSCode IPC DEBUG] Calling segmentTrack with:", {
            eventName: "VSCode IPC Event",
            properties: {
                ipc_event_type: eventType,
                ipc_direction: direction,
                ipc_time_since_start_ms: timeSinceStart,
                ipc_time_since_last_ms: timeSinceLast,
                ipc_total_events: totalEvents,
                ipc_events_per_second: eventsPerSecond,
                ...details,
            },
        });
        segmentTrack("VSCode IPC Event", {
            ipc_event_type: eventType,
            ipc_direction: direction,
            ipc_time_since_start_ms: timeSinceStart,
            ipc_time_since_last_ms: timeSinceLast,
            ipc_total_events: totalEvents,
            ipc_events_per_second: eventsPerSecond,
            ...details,
        });
        console.log("[VSCode IPC DEBUG] segmentTrack call completed");
    } else {
        console.log("[VSCode IPC DEBUG] No segmentTrack function available");
    }
}

/** Get current IPC statistics. Call from console: window.__getVSCodeIPCStats?.() */
export function getVSCodeIPCStats(): IPCStats & { eventsPerSecond: string; durationSeconds: number } {
    const duration = (Date.now() - ipcStats.startTime) / 1000;
    const totalEvents = Object.values(ipcStats.counts).reduce((a, b) => a + b, 0);
    return {
        ...ipcStats,
        eventsPerSecond: (totalEvents / duration).toFixed(2),
        durationSeconds: Math.round(duration),
    };
}

/** Reset IPC statistics. Call from console: window.__resetVSCodeIPCStats?.() */
export function resetVSCodeIPCStats(): void {
    ipcStats.startTime = Date.now();
    ipcStats.lastEventTime = Date.now();
    for (const key of Object.keys(ipcStats.counts) as IPCEventType[]) {
        ipcStats.counts[key] = 0;
    }
    // Clear debug mode cache to force re-check
    debugEnabledCache = null;
    if (debugCheckTimeout !== null) {
        clearTimeout(debugCheckTimeout);
        debugCheckTimeout = null;
    }
    console.log("[VSCode IPC] Stats reset");
}

// Expose stats functions globally for console debugging
if (typeof window !== "undefined") {
    (window as unknown as Record<string, unknown>).__getVSCodeIPCStats = getVSCodeIPCStats;
    (window as unknown as Record<string, unknown>).__resetVSCodeIPCStats = resetVSCodeIPCStats;
}

export type RPCMessage = {
    type: "rpc";
    id: string;
    service: string;
    method: string;
    args: unknown[];
};

export type RPCCancelMessage = {
    type: "rpc-cancel";
    id: string;
};

export type RPCRequest = RPCMessage | RPCCancelMessage;

export type RPCResponse = {
    type: "rpc-response";
    id: string;
    result?: unknown;
    error?: RPCError;
    done?: boolean;
};

export type RPCError = {
    code: number;
    message: string;
    details?: unknown;
};

const activeStreams = new Map<string, AbortController>();

export async function handleRPCMessage(
    api: Record<string, unknown>,
    message: RPCRequest,
): Promise<AsyncIterable<RPCResponse> | RPCResponse | void> {
    if (message.type === "rpc-cancel") {
        activeStreams.get(message.id)?.abort();
        return;
    }

    try {
        const service = api[message.service];
        if (!service) {
            throw new ConnectError("Service not found", Code.Unimplemented);
        }

        const methodName = message.method;
        const methodImpl = Reflect.get(service, methodName) as unknown;
        if (typeof methodImpl !== "function") {
            throw new ConnectError("Method not found", Code.Unimplemented);
        }

        const controller = new AbortController();
        const options = (message.args[1] as CallOptions | undefined) || {};
        options.signal = controller.signal;
        message.args[1] = options;
        activeStreams.set(message.id, controller);

        const result = (await methodImpl.apply(service, message.args)) as unknown;

        if (Symbol.asyncIterator in Object(result)) {
            return handleStreamingResponse(message, result as AsyncIterable<unknown>, () => {
                activeStreams.delete(message.id);
            });
        }

        activeStreams.delete(message.id);
        return {
            type: "rpc-response",
            id: message.id,
            result,
        };
    } catch (err) {
        activeStreams.delete(message.id);
        return {
            type: "rpc-response",
            id: message.id,
            error: serializeError(err),
        };
    }
}

async function* handleStreamingResponse(
    message: RPCMessage,
    result: AsyncIterable<unknown>,
    onComplete: () => void,
): AsyncIterable<RPCResponse> {
    try {
        for await (const item of result) {
            yield {
                type: "rpc-response",
                id: message.id,
                result: item,
            };
        }
        yield {
            type: "rpc-response",
            id: message.id,
            done: true,
        };
    } catch (err) {
        yield {
            type: "rpc-response",
            id: message.id,
            error: serializeError(err),
        };
    } finally {
        onComplete();
    }
}

function serializeError(err: unknown): RPCError {
    if (err instanceof Error && err.name === "AbortError") {
        err = new ConnectError("Canceled", Code.Canceled);
    }
    if (err instanceof ConnectError) {
        return {
            code: err.code,
            message: err.message,
            details: err.details,
        };
    }
    return {
        code: Code.Internal,
        message: err instanceof Error ? err.message : String(err),
    };
}

export type IVSCodeIPC = {
    openFile(filePath: string): Promise<void>;
    enableFollowMode(): Promise<void>;
    disableFollowMode(): Promise<void>;
    resolve(environmentId: string, userId: string): Promise<void>;
    setVisibilityState(visible: boolean): Promise<void>;
};

export type VSCodeIPCOptions = {
    /** Segment tracking function from useSegmentTrack(). Only used when debug mode is enabled. */
    segmentTrack?: SegmentTrackFn;
};

export class VSCodeIPC implements IVSCodeIPC {
    private channel: MessageChannel | undefined;
    private messageHandler: ((event: MessageEvent) => void) | undefined;
    private readonly exposedServices: Record<string, unknown>;
    private readonly segmentTrack?: SegmentTrackFn;

    // Promise that resolves when the message port is send to the vscode iframe.
    // Note that gitpod.requestMessagePort could happen more than once as the vscode iframe
    // will reload itself if the devcontainer.workspaceFolder gets resolved after resolveEditorURL is called
    private readonly messagePortPromise: Promise<void>;

    constructor(
        api: GitpodAPI,
        private readonly iframe: HTMLIFrameElement,
        private readonly theme: "light" | "dark",
        options?: VSCodeIPCOptions,
    ) {
        this.segmentTrack = options?.segmentTrack;
        this.exposedServices = {
            [AccountService.typeName]: api.accountService,
            [EnvironmentService.typeName]: api.environmentService,
            [EventService.typeName]: api.eventService,
            [EnvironmentAutomationService.typeName]: api.environmentAutomationService,
        };
        let resolve: () => void;
        this.messagePortPromise = new Promise<void>((r) => {
            resolve = r;
        });
        this.messageHandler = (event: MessageEvent) => {
            // Only accept messages from our iframe
            if (event.source !== this.iframe.contentWindow) {
                return;
            }

            if (event.data?.type === "gitpod.requestMessagePort") {
                this.channel?.port1.close();
                this.channel?.port2.close();
                this.channel = new MessageChannel();
                const { port1, port2 } = this.channel;

                // Keep port1 for handling messages
                this.setupMessageHandler(port1);

                // Send port2 to the iframe with theme information
                this.iframe.contentWindow?.postMessage(
                    {
                        type: "gitpod.messagePort",
                        theme: this.theme,
                    },
                    "*",
                    [port2],
                );
                trackIPCEvent("messagePort", "outbound", this.segmentTrack, { theme: this.theme });
                resolve();
            }
        };

        window.addEventListener("message", this.messageHandler);
    }

    async openFile(filePath: string) {
        await this.messagePortPromise;
        this.channel?.port1.postMessage({
            type: "gitpod.agentExecution",
            action: "open-file",
            filePath,
        });
        trackIPCEvent("openFile", "outbound", this.segmentTrack, { filePath });
    }

    async enableFollowMode() {
        await this.messagePortPromise;
        this.channel?.port1.postMessage({
            type: "gitpod.agentExecution",
            action: "enableFollowMode",
        });
        trackIPCEvent("enableFollowMode", "outbound", this.segmentTrack);
    }

    async disableFollowMode() {
        await this.messagePortPromise;
        this.channel?.port1.postMessage({
            type: "gitpod.agentExecution",
            action: "disableFollowMode",
        });
        trackIPCEvent("disableFollowMode", "outbound", this.segmentTrack);
    }

    async resolve(environmentId: string, userId: string) {
        await this.messagePortPromise;
        this.channel?.port1.postMessage({
            type: "gitpod.agentExecution",
            action: "resolve",
            environmentId,
            userId,
        });
        trackIPCEvent("resolve", "outbound", this.segmentTrack, { environmentId, userId });
    }

    async setVisibilityState(visible: boolean) {
        await this.messagePortPromise;
        this.channel?.port1.postMessage({
            type: "gitpod.visibilityState",
            visible,
        });
        trackIPCEvent("setVisibilityState", "outbound", this.segmentTrack, { visible });
    }

    private setupMessageHandler(port: MessagePort) {
        port.onmessage = async (event) => {
            const message = event.data as RPCMessage;
            if (message.type !== "rpc") {
                return;
            }

            trackIPCEvent("rpcRequest", "inbound", this.segmentTrack, {
                service: message.service,
                method: message.method,
                id: message.id,
            });

            try {
                const responseOrStream = await handleRPCMessage(this.exposedServices, message);
                if (responseOrStream === undefined) {
                    return;
                }

                if (Symbol.asyncIterator in Object(responseOrStream)) {
                    for await (const response of responseOrStream as AsyncIterable<RPCResponse>) {
                        port.postMessage(response);
                        trackIPCEvent("rpcResponse", "outbound", this.segmentTrack, {
                            id: response.id,
                            hasError: !!response.error,
                            done: response.done,
                        });
                    }
                } else {
                    port.postMessage(responseOrStream as RPCResponse);
                    trackIPCEvent("rpcResponse", "outbound", this.segmentTrack, {
                        id: (responseOrStream as RPCResponse).id,
                        hasError: !!(responseOrStream as RPCResponse).error,
                    });
                }
            } catch (err) {
                console.error("Error handling RPC message:", err);
            }
        };

        port.start();
    }

    dispose() {
        if (this.messageHandler) {
            window.removeEventListener("message", this.messageHandler);
            this.messageHandler = undefined;
        }
        this.channel?.port1.close();
        this.channel?.port2.close();
    }
}
